import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Tabs, TabPanel } from "@/components/Tabs";

const TABS = [
  { id: "unassigned", label: "Unassigned audits" },
  { id: "assigned", label: "Assigned audits" },
];

const AuthenticatedTag = () => (
  <span className="ml-2 border border-[#16324F] text-[#16324F] px-1.5 py-0.5 rounded-sm text-[10px] uppercase">Authenticated</span>
);

const UnassignedTable = ({ audits, sites, selection, setSelection, assign, discard }) => (
  <div className="table-card" tabIndex={0} role="region" aria-label="Unassigned audits awaiting site assignment">
    <table className="w-full min-w-[900px] text-left border-collapse text-sm" data-testid="unassigned-table">
      <caption className="sr-only">Unassigned audits awaiting site assignment</caption>
      <thead>
        <tr className="border-b border-gray-300">
          <th scope="col" className="px-5 py-3 font-heading font-semibold">Page</th>
          <th scope="col" className="px-5 py-3 font-heading font-semibold text-right">Violations</th>
          <th scope="col" className="px-5 py-3 font-heading font-semibold text-right">When</th>
          <th scope="col" className="px-5 py-3 font-heading font-semibold">Assign to site</th>
          <th scope="col" className="px-5 py-3 font-heading font-semibold text-right">Actions</th>
        </tr>
      </thead>
      <tbody>
        {audits.length === 0 && (
          <tr><td colSpan={5} className="px-5 py-10 text-center text-ink-muted" data-testid="unassigned-empty">The staging area is clear.</td></tr>
        )}
        {audits.map((a) => (
          <tr key={a.id} className="border-b border-gray-200 last:border-0" data-testid={`unassigned-row-${a.id}`}>
            <td className="px-5 py-3 font-mono text-xs max-w-[280px] truncate" title={a.page_url}>
              {a.page_url}
              {a.authenticated && <AuthenticatedTag />}
            </td>
            <td className="px-5 py-3 font-mono text-right">{a.totals?.total ?? 0}</td>
            <td className="px-5 py-3 font-mono text-xs text-right text-ink-muted">{new Date(a.created_at).toLocaleString()}</td>
            <td className="px-5 py-3">
              <label htmlFor={`assign-${a.id}`} className="sr-only">Site for this audit</label>
              <select
                id={`assign-${a.id}`}
                data-testid={`assign-site-select-${a.id}`}
                value={selection[a.id] || ""}
                onChange={(e) => setSelection({ ...selection, [a.id]: e.target.value })}
                className="border border-gray-300 rounded-sm px-2 py-1.5 text-xs bg-white focus:ring-2 focus:ring-[#16324F] outline-none min-w-[180px]"
              >
                <option value="">Choose site</option>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.client_name} / {s.name}</option>)}
              </select>
            </td>
            <td className="px-5 py-3 text-right whitespace-nowrap">
              <button data-testid={`assign-btn-${a.id}`} onClick={() => assign(a.id)}
                className="bg-[#16324F] text-white rounded-sm px-3 py-1.5 text-xs hover:bg-[#10243a] transition-colors mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] focus-visible:ring-offset-2">
                Assign
              </button>
              <button data-testid={`discard-btn-${a.id}`} onClick={() => discard(a.id)}
                className="text-xs text-[#C62828] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] rounded-sm px-1">
                Discard
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const AssignedTable = ({ audits }) => (
  <div className="table-card" tabIndex={0} role="region" aria-label="Audits already attached to a site">
    <table className="w-full min-w-[720px] text-left border-collapse text-sm" data-testid="assigned-table">
      <caption className="sr-only">Audits already attached to a site</caption>
      <thead>
        <tr className="border-b border-gray-300">
          <th scope="col" className="px-5 py-3 font-heading font-semibold">Page</th>
          <th scope="col" className="px-5 py-3 font-heading font-semibold">Site</th>
          <th scope="col" className="px-5 py-3 font-heading font-semibold text-right">Violations</th>
          <th scope="col" className="px-5 py-3 font-heading font-semibold text-right">When</th>
        </tr>
      </thead>
      <tbody>
        {audits.length === 0 && (
          <tr><td colSpan={4} className="px-5 py-10 text-center text-ink-muted" data-testid="assigned-empty">No audit has been assigned to a site yet.</td></tr>
        )}
        {audits.map((a) => (
          <tr key={a.id} className="border-b border-gray-200 last:border-0" data-testid={`assigned-row-${a.id}`}>
            <td className="px-5 py-3 font-mono text-xs max-w-[280px] truncate" title={a.page_url}>
              {a.page_url}
              {a.authenticated && <AuthenticatedTag />}
            </td>
            <td className="px-5 py-3">
              <Link to={`/sites/${a.site_id}`} className="text-[#16324F] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] rounded-sm">
                {a.site_name || "Site"}
              </Link>
            </td>
            <td className="px-5 py-3 font-mono text-right">{a.totals?.total ?? 0}</td>
            <td className="px-5 py-3 font-mono text-xs text-right text-ink-muted">{new Date(a.created_at).toLocaleString()}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function Audits() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("tab");
  const activeTab = TABS.some((t) => t.id === requested) ? requested : "unassigned";

  const [audits, setAudits] = useState([]);
  const [sites, setSites] = useState([]);
  const [selection, setSelection] = useState({});

  // One request covers both tabs: /audits returns assigned and unassigned rows,
  // so switching tabs never costs a round trip.
  const load = useCallback(() => {
    api.get("/audits").then((r) => setAudits(r.data)).catch(() => toast.error("Could not load audits"));
    api.get("/sites").then((r) => setSites(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const unassigned = useMemo(() => audits.filter((a) => !a.site_id), [audits]);
  const assigned = useMemo(() => audits.filter((a) => a.site_id), [audits]);
  const tabsWithCounts = useMemo(
    () => TABS.map((t) => ({ ...t, badge: (t.id === "unassigned" ? unassigned : assigned).length })),
    [unassigned, assigned],
  );

  const assign = async (auditId) => {
    const siteId = selection[auditId];
    if (!siteId) return toast.error("Choose a site first");
    try {
      const { data } = await api.post(`/audits/${auditId}/assign`, { site_id: siteId });
      const r = data.reconciliation;
      toast.success(`Assigned. ${r.new} new, ${r.still_open} still open, ${r.fixed} fixed, ${r.regressed} regressed.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not assign");
    }
  };

  const discard = async (auditId) => {
    await api.delete(`/audits/${auditId}`);
    toast.success("Audit discarded");
    load();
  };

  const selectTab = (id) => setSearchParams(id === "unassigned" ? {} : { tab: id }, { replace: true });

  return (
    <div data-testid="audits-page">
      <PageHeader
        title="Audits"
        description="Every audit run from the extension. Assign the unattached ones to start tracking their issues."
      >
        <Tabs
          tabs={tabsWithCounts}
          active={activeTab}
          onChange={selectTab}
          label="Audit views"
          idPrefix="audits"
        />
      </PageHeader>

      <TabPanel id="unassigned" active={activeTab} idPrefix="audits">
        <UnassignedTable
          audits={unassigned}
          sites={sites}
          selection={selection}
          setSelection={setSelection}
          assign={assign}
          discard={discard}
        />
      </TabPanel>
      <TabPanel id="assigned" active={activeTab} idPrefix="audits">
        <AssignedTable audits={assigned} />
      </TabPanel>
    </div>
  );
}
